/**
 * One platform variant: live preview on top, the editor underneath, and the
 * approve / schedule / publish footer.
 *
 * Everything here edits the local draft buffer — the parent autosaves. Once a
 * variant is published (or publishing) the API refuses edits, so the editor
 * locks itself to match.
 */

import { useState } from 'react';
import {
  AlertCircle,
  Check,
  CircleSlash,
  Clock,
  ExternalLink,
  Loader2,
  Plug,
  Send,
  TriangleAlert,
  Wand2,
} from 'lucide-react';
import { PLATFORM_SPECS } from '../../../shared/platforms';
import type {
  MediaAsset,
  PlatformPost,
  PlatformPostMeta,
  SocialAccount,
} from '../../../shared/types';
import { slugify } from '../../../shared/utils';
import {
  Badge,
  Button,
  Card,
  DateTimePicker,
  Field,
  Input,
  Select,
  Switch,
  Textarea,
  Tooltip,
} from '../ui';
import { PLATFORM_POST_STATUS_TONE, formatWhen, platformPostStatusLabel } from '../../lib/format';
import { cn, isModifierEnter } from '../../lib/utils';
import { AiToolbar } from './AiToolbar';
import type { AiAction } from './AiToolbar';
import { CharCounter } from './CharCounter';
import { HashtagEditor } from './HashtagEditor';
import { PlatformPreview } from './PlatformPreview';
import { ValidationNotes } from './ValidationNotes';
import {
  addHashtags,
  assetUrl,
  danglingHashtags,
  draftHashtags,
  issuesFor,
  removeHashtag,
  variantAssets,
} from './composerUtils';
import type { DraftValue, SaveState } from './types';

export interface PlatformEditorProps {
  variant: PlatformPost;
  value: DraftValue;
  saveState: SaveState;
  account: SocialAccount | null;
  accounts: SocialAccount[];
  postAssets: MediaAsset[];
  timezone: string;
  busyAi: AiAction | null;
  publishing: boolean;
  statusBusy: boolean;
  onChange: (
    next: DraftValue | ((current: DraftValue) => DraftValue),
    options?: { immediate?: boolean },
  ) => void;
  onImprove: (instruction: string) => void;
  onRegenerate: (instructions?: string) => void;
  onHashtags: () => void;
  onGenerateImage: () => void;
  onApproveChange: (approved: boolean) => void;
  onSchedule: (isoValue: string | null) => void;
  onPublishNow: () => void;
  onSelectAccount: (accountId: string) => void;
}

function SaveIndicator({ state }: { state: SaveState }) {
  if (state === 'clean') return null;
  const map: Record<Exclude<SaveState, 'clean'>, { label: string; className: string }> = {
    dirty: { label: 'Unsaved changes', className: 'text-subtle' },
    saving: { label: 'Saving…', className: 'text-subtle' },
    saved: { label: 'Saved', className: 'text-success' },
    error: { label: 'Could not save', className: 'text-danger' },
  };
  const entry = map[state];
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[11px]', entry.className)}>
      {state === 'saving' ? (
        <Loader2 className="size-3 animate-spin" aria-hidden />
      ) : state === 'saved' ? (
        <Check className="size-3" aria-hidden />
      ) : state === 'error' ? (
        <AlertCircle className="size-3" aria-hidden />
      ) : (
        <span className="size-1.5 rounded-full bg-current" aria-hidden />
      )}
      {entry.label}
    </span>
  );
}

export function PlatformEditor({
  variant,
  value,
  saveState,
  account,
  accounts,
  postAssets,
  timezone,
  busyAi,
  publishing,
  statusBusy,
  onChange,
  onImprove,
  onRegenerate,
  onHashtags,
  onGenerateImage,
  onApproveChange,
  onSchedule,
  onPublishNow,
  onSelectAccount,
}: PlatformEditorProps) {
  const spec = PLATFORM_SPECS[variant.platform];
  const [showSlug, setShowSlug] = useState(false);

  const locked = variant.status === 'published' || variant.status === 'publishing';
  const platformAccounts = accounts.filter((item) => item.platform === variant.platform);

  const selectedAssets = variantAssets(value, postAssets);
  const tags = draftHashtags(value);
  const missingTags = danglingHashtags(value);
  const issues = issuesFor(variant, value, postAssets);
  const blocking = issues.some((issue) => issue.level === 'error');

  const setMeta = (patch: Partial<PlatformPostMeta>, immediate = false) => {
    onChange((current) => ({ ...current, meta: { ...current.meta, ...patch } }), { immediate });
  };

  const toggleAsset = (assetId: string) => {
    onChange(
      (current) => {
        const explicit = current.media.length
          ? current.media
          : postAssets.map((asset) => ({ assetId: asset.id }));
        const has = explicit.some((item) => item.assetId === assetId);
        const next = has
          ? explicit.filter((item) => item.assetId !== assetId)
          : [...explicit, { assetId }].slice(0, spec.limits.maxImages);
        return { ...current, media: next };
      },
      { immediate: true },
    );
  };

  const scheduleLabel = variant.scheduledAt
    ? `Scheduled for ${formatWhen(variant.scheduledAt, timezone)}`
    : null;

  return (
    <div className="space-y-4">
      {/* Status strip -------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={PLATFORM_POST_STATUS_TONE[variant.status]} dot pulse={variant.status === 'publishing'}>
          {platformPostStatusLabel(variant.status)}
        </Badge>

        {variant.simulated && (
          <Tooltip content="Published in simulation — no live post was created.">
            <Badge tone="warning">Simulated</Badge>
          </Tooltip>
        )}

        {account && !variant.socialAccountId ? (
          <Tooltip content="This variant was drafted before the account was connected.">
            <Button
              variant="soft"
              size="xs"
              disabled={locked || statusBusy}
              onClick={() => onSelectAccount(account.id)}
              leftIcon={<Plug className="size-3" />}
            >
              Publish to {account.displayName}
            </Button>
          </Tooltip>
        ) : account ? (
          platformAccounts.length > 1 && !locked ? (
            <Select
              selectSize="sm"
              value={variant.socialAccountId ?? ''}
              onChange={(event) => onSelectAccount(event.target.value)}
              wrapperClassName="w-auto min-w-44"
              aria-label={`${spec.shortName} account`}
              options={platformAccounts.map((item) => ({
                value: item.id,
                label: item.displayName,
              }))}
            />
          ) : (
            <span className="truncate text-xs text-muted">
              Publishing to <span className="text-fg">{account.displayName}</span>
              {account.status === 'simulated' && ' (simulated)'}
            </span>
          )
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-warning">
            <TriangleAlert className="size-3.5" aria-hidden />
            No {spec.shortName} account connected
          </span>
        )}

        <div className="ml-auto flex items-center gap-3">
          {scheduleLabel && (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-info">
              <Clock className="size-3" aria-hidden />
              {scheduleLabel}
            </span>
          )}
          <SaveIndicator state={saveState} />
        </div>
      </div>

      {variant.error && variant.status === 'failed' && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
          <span className="leading-relaxed">{variant.error}</span>
        </div>
      )}

      {/* Preview ------------------------------------------------------- */}
      <PlatformPreview
        platform={variant.platform}
        content={value.content}
        meta={value.meta}
        media={selectedAssets}
        accountName={account?.displayName ?? null}
        accountHandle={
          (account?.metadata.username as string | undefined) ??
          (account?.metadata.wpUsername as string | undefined) ??
          null
        }
        avatarUrl={account?.avatarUrl ?? null}
        {...(variant.publishedAt
          ? { timestamp: formatWhen(variant.publishedAt, timezone) }
          : variant.scheduledAt
            ? { timestamp: formatWhen(variant.scheduledAt, timezone) }
            : {})}
      />

      {/* Editor -------------------------------------------------------- */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2.5">
          <AiToolbar
            platform={variant.platform}
            busy={busyAi}
            onImprove={onImprove}
            onRegenerate={onRegenerate}
            onHashtags={onHashtags}
            onGenerateImage={onGenerateImage}
            disabled={locked}
          />
          <CharCounter platform={variant.platform} text={value.content} />
        </div>

        <div className="space-y-4 px-4 py-4">
          {spec.features.title && (
            <Field
              label="Title"
              required
              labelAction={
                <button
                  type="button"
                  onClick={() => setShowSlug((current) => !current)}
                  className="text-[11px] text-subtle underline-offset-2 hover:text-muted hover:underline"
                >
                  {showSlug ? 'Hide slug' : 'Edit slug'}
                </button>
              }
            >
              <Input
                value={value.meta.title ?? ''}
                disabled={locked}
                aria-label={`${spec.shortName} title`}
                placeholder="Headline for the article"
                onChange={(event) => setMeta({ title: event.target.value })}
                invalid={issues.some((issue) => issue.field === 'title')}
              />
            </Field>
          )}

          {spec.features.slug && showSlug && (
            <Field
              label="Slug"
              hint="The URL segment on your site. Leave it empty and WordPress will derive one."
            >
              <div className="flex gap-2">
                <Input
                  value={value.meta.slug ?? ''}
                  disabled={locked}
                  placeholder="my-post-title"
                  onChange={(event) => setMeta({ slug: slugify(event.target.value) })}
                />
                <Button
                  variant="secondary"
                  disabled={locked || !value.meta.title?.trim()}
                  onClick={() => setMeta({ slug: slugify(value.meta.title ?? '') }, true)}
                >
                  From title
                </Button>
              </div>
            </Field>
          )}

          <Field
            label={spec.kind === 'blog' ? 'Article body' : 'Caption'}
            labelAction={
              locked ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] text-subtle">
                  <CircleSlash className="size-3" aria-hidden />
                  Published variants are read-only
                </span>
              ) : null
            }
          >
            <Textarea
              value={value.content}
              disabled={locked}
              aria-label={`${spec.shortName} ${spec.kind === 'blog' ? 'article body' : 'caption'}`}
              rows={spec.kind === 'blog' ? 12 : 6}
              maxRows={spec.kind === 'blog' ? 28 : 16}
              placeholder={
                spec.kind === 'blog'
                  ? 'Write the article, or generate a draft from your notes.'
                  : `Write the ${spec.shortName} version, or generate one from your notes.`
              }
              onChange={(event) => onChange((current) => ({ ...current, content: event.target.value }))}
              onKeyDown={(event) => {
                if (isModifierEnter(event)) {
                  event.preventDefault();
                  onChange((current) => current, { immediate: true });
                }
              }}
              invalid={issues.some((issue) => issue.field === 'content' && issue.level === 'error')}
              className={cn('leading-relaxed', spec.kind === 'blog' && 'font-mono text-[13px]')}
            />
          </Field>

          {spec.features.hashtags && (
            <Field label="Hashtags">
              <HashtagEditor
                platform={variant.platform}
                tags={tags}
                missing={missingTags}
                disabled={locked}
                onAdd={(next) => onChange((current) => addHashtags(current, next))}
                onRemove={(tag) => onChange((current) => removeHashtag(current, tag))}
                action={
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={locked}
                    loading={busyAi === 'hashtags'}
                    onClick={onHashtags}
                    leftIcon={<Wand2 className="size-3" />}
                  >
                    Suggest
                  </Button>
                }
              />
            </Field>
          )}

          {spec.features.excerpt && (
            <Field label="Excerpt" hint="Shown in listings and social cards.">
              <Textarea
                value={value.meta.excerpt ?? ''}
                disabled={locked}
                rows={2}
                maxRows={5}
                placeholder="One or two sentences that sell the click."
                onChange={(event) => setMeta({ excerpt: event.target.value })}
              />
            </Field>
          )}

          {spec.features.categories && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Categories" hint="Comma separated.">
                <Input
                  value={(value.meta.categories ?? []).join(', ')}
                  disabled={locked}
                  placeholder="Announcements, Guides"
                  onChange={(event) =>
                    setMeta({
                      categories: event.target.value
                        .split(',')
                        .map((entry) => entry.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </Field>
              <Field label="Tags" hint="Comma separated.">
                <Input
                  value={(value.meta.tags ?? []).join(', ')}
                  disabled={locked}
                  placeholder="product, launch"
                  onChange={(event) =>
                    setMeta({
                      tags: event.target.value
                        .split(',')
                        .map((entry) => entry.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </Field>
            </div>
          )}

          {spec.features.firstComment && (
            <Field
              label="First comment"
              hint="Posted immediately after the image — the usual home for hashtags."
            >
              <Textarea
                value={value.meta.firstComment ?? ''}
                disabled={locked}
                rows={2}
                maxRows={6}
                placeholder="Add context, credits or the hashtag block."
                onChange={(event) => setMeta({ firstComment: event.target.value })}
              />
            </Field>
          )}

          {(variant.platform === 'facebook' || variant.platform === 'linkedin') && (
            <Field
              label="Link card"
              hint={`Optional — ${spec.shortName} renders a preview card for this URL.`}
            >
              <Input
                type="url"
                value={value.meta.linkUrl ?? ''}
                disabled={locked}
                placeholder="https://example.com/announcement"
                onChange={(event) => setMeta({ linkUrl: event.target.value })}
              />
            </Field>
          )}

          {spec.features.images && postAssets.length > 0 && (
            <Field
              label="Media for this variant"
              labelAction={
                value.media.length > 0 && !locked ? (
                  <Button
                    variant="link"
                    size="xs"
                    onClick={() => onChange((current) => ({ ...current, media: [] }), { immediate: true })}
                  >
                    Use every attachment
                  </Button>
                ) : null
              }
              hint={
                value.media.length === 0
                  ? `Using all ${postAssets.length} attachment${postAssets.length === 1 ? '' : 's'} — ${spec.shortName} takes up to ${spec.limits.maxImages}.`
                  : `${value.media.length} of ${postAssets.length} selected.`
              }
            >
              <div className="flex flex-wrap gap-2">
                {postAssets.map((asset) => {
                  const active = selectedAssets.some((item) => item.id === asset.id);
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      disabled={locked}
                      onClick={() => toggleAsset(asset.id)}
                      aria-pressed={active}
                      className={cn(
                        'relative size-14 overflow-hidden rounded-md border transition-[border-color,opacity]',
                        active
                          ? 'border-accent ring-2 ring-accent/25'
                          : 'border-border opacity-55 hover:opacity-85',
                        locked && 'cursor-not-allowed',
                      )}
                    >
                      {asset.kind === 'image' ? (
                        <img
                          src={assetUrl(asset)}
                          alt={asset.altText ?? asset.filename}
                          loading="lazy"
                          className="size-full object-cover"
                        />
                      ) : (
                        <span className="flex size-full items-center justify-center bg-sunken text-[10px] text-subtle">
                          Video
                        </span>
                      )}
                      {active && (
                        <span className="absolute right-0.5 top-0.5 flex size-4 items-center justify-center rounded-full bg-accent text-accent-fg">
                          <Check className="size-2.5" aria-hidden />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          <ValidationNotes issues={issues} />
        </div>

        {/* Footer ------------------------------------------------------ */}
        <div className="flex flex-wrap items-center gap-3 border-t border-border bg-elevated/30 px-4 py-3">
          {variant.status === 'published' ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-xs text-success">
                <Check className="size-3.5" aria-hidden />
                Published {variant.publishedAt ? formatWhen(variant.publishedAt, timezone) : ''}
              </span>
              {variant.externalUrl && (
                <a
                  href={variant.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-accent underline-offset-4 hover:underline"
                >
                  View post
                  <ExternalLink className="size-3" aria-hidden />
                </a>
              )}
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-2 text-xs text-muted">
                <Switch
                  size="sm"
                  checked={variant.status === 'approved' || variant.status === 'scheduled'}
                  disabled={locked || statusBusy || variant.status === 'scheduled'}
                  onCheckedChange={onApproveChange}
                  label={`Approve the ${spec.shortName} variant`}
                />
                {variant.status === 'scheduled' ? 'Locked in' : 'Approved'}
              </span>

              <div className="min-w-[16rem] flex-1">
                <DateTimePicker
                  value={variant.scheduledAt ? new Date(variant.scheduledAt).toISOString() : null}
                  onChange={onSchedule}
                  timezone={timezone}
                  disabled={locked || statusBusy}
                  min={new Date(Date.now() + 60_000).toISOString()}
                  placeholder="Schedule this one"
                />
              </div>

              <Tooltip
                content={
                  blocking
                    ? 'Fix the errors above first.'
                    : `Publish to ${account?.displayName ?? spec.shortName} right now`
                }
              >
                <Button
                  size="sm"
                  disabled={locked || blocking || publishing}
                  loading={publishing}
                  onClick={onPublishNow}
                  leftIcon={<Send className="size-3.5" />}
                >
                  Publish now
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
